import React, { useState, useEffect } from 'react';
import { css } from '@emotion/react';
import { useTranslation, Trans } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Heading, Text, PrimaryButton, ExternalLinkIcon } from '@frontend/design-system';
import { BuyNowPayLaterPortal } from './portal';

type MerchantStatusObjType = {
  header: string | null;
  subtitle: string | null;
  ctaText: string | null;
  ctaURL: string | URL | undefined;
  subtext?: React.ReactNode | undefined;
};

export const BuyNowPayLaterStatus = ({
  merchantStatus,
  merchantUrl,
  locationId,
}: {
  merchantStatus: string | null;
  merchantUrl: string | URL | undefined;
  locationId: string | undefined;
}) => {
  const { t } = useTranslation('payments');
  const [merchantStatusObj, setMerchantStatusObj] = useState<MerchantStatusObjType>({
    header: null,
    subtitle: null,
    ctaText: null,
    ctaURL: undefined,
    subtext: undefined,
  });
  const adminURL = 'https://partners.sunbit.com/login';
  const getStatusContext = () => {
    switch (merchantStatus) {
      case 'ACTIVATED':
        setMerchantStatusObj({
          header: t('View Transactions'),
          subtitle: t('Visit your Sunbit dashboard to view buy now, pay-over-time invoices and more.'),
          subtext: (
            <Trans t={t}>
              <Text color='light' size='medium'>
                For login details, please check the email address you provided during Sunbit account activation for an
                email from Sunbit.
              </Text>
            </Trans>
          ),
          ctaText: t('Sunbit Dashboard'),
          ctaURL: adminURL,
        });
        break;
      case 'DECLINED':
        setMerchantStatusObj({
          header: t('Contact Sunbit'),
          subtitle: t(
            'Your request for account activation has been declined. Please contact Sunbit for more information. Until your account is activated, you will be unable to offer buy now, pay-over-time from within Weave.'
          ),
          subtext: (
            <Trans t={t}>
              <Text color='light' size='medium'>
                Call{' '}
                <span
                  css={css`
                    font-weight: ${theme.font.weight.bold};
                  `}
                >
                  855-778-6248
                </span>{' '}
                for more details.
              </Text>
            </Trans>
          ),
          ctaText: null,
          ctaURL: undefined,
        });
        break;
      case 'SUBMITTED':
        setMerchantStatusObj({
          header: t('Application Submitted'),
          subtitle: t(
            'Sunbit is reviewing your application. Check back later for updates. Once your account is activated, you will be able to offer buy now, pay-over-time from within Weave.'
          ),
          ctaText: null,
          ctaURL: undefined,
        });
        break;
      case 'CREATED':
      default:
        setMerchantStatusObj({
          header: t('Finish Activation'),
          subtitle: t(
            'Click below to visit Sunbit and finish activating. Once you have activated, you will be able to offer buy now, pay-over-time from within Weave.'
          ),
          ctaText: t('Finish Sunbit Activation'),
          ctaURL: merchantUrl,
        });
        break;
    }
  };

  useEffect(() => {
    getStatusContext();
  }, [merchantStatus]);

  return (
    <div
      css={css`
        width: 100%;
        display: flex;
        flex-direction: column;
        margin-top: ${theme.spacing(6)};
      `}
    >
      <div
        css={css`
          max-width: ${theme.spacing(58)};
          min-width: ${theme.spacing(20)};
          display: flex;
          flex-direction: column;
        `}
      >
        <Heading level={2}>{merchantStatusObj.header}</Heading>
        <Text color='light' size='medium'>
          {merchantStatusObj.subtitle}
        </Text>
        {merchantStatusObj.subtext}
      </div>
      {merchantStatusObj.ctaText && (
        <PrimaryButton
          onClick={() => window.open(merchantStatusObj?.ctaURL, '_blank')}
          trackingId='buy-now-pay-later-dashboard-button'
          css={css`
            max-width: ${theme.spacing(28)};
            min-width: ${theme.spacing(10)};
            margin-top: ${theme.spacing(2)};
            width: 100%;
          `}
        >
          {merchantStatusObj.ctaText}
          <ExternalLinkIcon size={16} />
        </PrimaryButton>
      )}
      {merchantStatus === 'ACTIVATED' && <BuyNowPayLaterPortal locationId={locationId} />}
    </div>
  );
};
